import React, { useEffect, useRef, useState, useCallback } from 'react'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

// ─── Types ────────────────────────────────────────────────────────────────────
interface Props {
  token:string; balance:number|null; playerId?:string; operatorId?:string
}
type BetType = 'red'|'green'|'violet'|'number'|'small'|'big'
interface PlacedBet { type:BetType; value?:number; stake:number }
interface HistoryItem { number:number; colors:string[] }

// ─── Color rules ──────────────────────────────────────────────────────────────
const getColors=(n:number)=>{
  if(n===0) return ['violet','red']
  if(n===5) return ['violet','green']
  return n%2===0?['red']:['green']
}
const numBg=(n:number)=>{
  const c=getColors(n)
  if(c.includes('violet')&&c.includes('red'))   return 'linear-gradient(135deg,#8b5cf6,#ef4444)'
  if(c.includes('violet')&&c.includes('green')) return 'linear-gradient(135deg,#8b5cf6,#22c55e)'
  if(c.includes('red'))   return '#dc2626'
  return '#16a34a'
}
const PAYOUT_LABEL:Record<BetType,string>={
  red:'2×',green:'2×',violet:'4.5×',number:'9×',small:'2×',big:'2×'
}

// ─── Countdown ring ───────────────────────────────────────────────────────────
function CountdownRing({ms,totalMs}:{ms:number;totalMs:number}){
  const pct  = Math.max(0, ms/totalMs)
  const r    = 44, circ = 2*Math.PI*r
  const dash = circ*pct
  const secs = Math.ceil(ms/1000)
  const col  = pct>0.5?'#22c55e':pct>0.2?'#f59e0b':'#ef4444'
  return(
    <div style={{position:'relative',width:110,height:110,margin:'0 auto'}}>
      <svg width="110" height="110" style={{transform:'rotate(-90deg)'}}>
        <circle cx="55" cy="55" r={r} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="7"/>
        <circle cx="55" cy="55" r={r} fill="none" stroke={col} strokeWidth="7"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{transition:'stroke-dasharray .9s linear, stroke .5s'}}/>
      </svg>
      <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',
        alignItems:'center',justifyContent:'center'}}>
        <span style={{fontSize:28,fontWeight:900,color:col,lineHeight:1}}>{secs}</span>
        <span style={{fontSize:9,color:'rgba(255,255,255,.4)'}}>seconds</span>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export function ColorPrediction({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const socketRef  = useRef<Socket|null>(null)
  const sessionRef = useRef({playerId:initPid||'',operatorId:initOid||''})

  const [phase,setPhase]         = useState<'betting'|'revealing'|'result'>('betting')
  const [balance,setBalance]     = useState<number|null>(initBalance)
  const [countdownMs,setCountdownMs] = useState(30000)
  const [roundId,setRoundId]     = useState('')
  const [history,setHistory]     = useState<HistoryItem[]>([])
  const [result,setResult]       = useState<{number:number;colors:string[]}|null>(null)
  const [myResult,setMyResult]   = useState<{total_win:number;settled:any[];number:number}|null>(null)

  const [selectedType,setSelectedType] = useState<BetType>('red')
  const [selectedNum,setSelectedNum]   = useState<number|null>(null)
  const [stake,setStake]         = useState(10)
  const [pendingBets,setPendingBets]   = useState<PlacedBet[]>([])
  const [betPlaced,setBetPlaced] = useState(false)
  const [toast,setToast]         = useState('')
  const [currency,setCurrency]   = useState('coins')
  const [winAnim,setWinAnim]     = useState(false)

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,d=2500)=>{ setToast(msg); setTimeout(()=>setToast(''),d) }

  const totalStake = pendingBets.reduce((s,b)=>s+b.stake,0)

  // ── Socket ──────────────────────────────────────────────────────────────────
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json(); if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    s.on('cp:new_round',(d:any)=>{
      setPhase('betting'); setRoundId(d.round_id)
      setCountdownMs(d.countdown_ms||30000)
      setResult(null); setMyResult(null); setBetPlaced(false)
      setPendingBets([]); setWinAnim(false)
      if(d.history) setHistory(d.history)
    })

    s.on('cp:tick',(d:any)=>{ setCountdownMs(d.countdown_ms) })

    s.on('cp:revealing',()=>{ setPhase('revealing') })

    s.on('cp:result',(d:any)=>{
      setResult({number:d.number,colors:d.colors})
      setHistory(d.history||[])
      setPhase('result')
      Sounds.tick()
    })

    s.on('cp:my_result',(d:any)=>{
      setMyResult({total_win:d.total_win,settled:d.settled,number:d.number})
      if(typeof d.balance==='number') setBalance(d.balance)
      if(d.total_win>0){
        setWinAnim(true); Sounds.cashout()
        showToast(`🎉 WIN ₹${d.total_win.toFixed(2)}!`,3000)
      } else if(betPlaced){
        Sounds.cooked()
        showToast(`💔 No win this round`,2000)
      }
    })

    s.on('cp:bet_placed',(d:any)=>{
      setBetPlaced(true)
      showToast(`✅ Bet placed! ₹${d.total_stake}`,1500)
    })
    s.on('cp:error',(d:any)=>{ showToast(`❌ ${d.error}`) })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return()=>{ s.disconnect() }
  },[token])

  // ── Add bet ────────────────────────────────────────────────────────────────
  const addBet=useCallback(()=>{
    if(betPlaced) return showToast('Bet already placed this round')
    if(phase!=='betting') return showToast('Betting is closed')
    if(countdownMs<3000) return showToast('Too late!')
    if(balance!==null&&balance<(totalStake+stake)) return showToast('Not enough balance')

    const type = selectedType==='number' && selectedNum!==null ? 'number' : selectedType
    if(type==='number'&&selectedNum===null) return showToast('Pick a number first')

    const newBet:PlacedBet = {type, value:type==='number'?selectedNum!:undefined, stake}
    setPendingBets(p=>[...p,newBet])
  },[betPlaced,phase,countdownMs,balance,totalStake,stake,selectedType,selectedNum])

  // ── Place all bets ─────────────────────────────────────────────────────────
  const placeBets=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||pendingBets.length===0) return showToast('Add bets first')
    if(balance!==null&&balance<totalStake) return showToast('Not enough balance')
    setBalance(b=>b!==null?Math.max(0,b-totalStake):b)
    s.emit('cp:bet',{player_id:playerId,operator_id:operatorId,bets:pendingBets})
  }

  const STAKES=[1,5,10,20,50,100,500]

  const colorBtn=(type:BetType,label:string,bg:string,border:string,payout:string)=>(
    <button key={type}
      onClick={()=>{ setSelectedType(type); if(type!=='number') setSelectedNum(null) }}
      style={{
        flex:1,padding:'10px 4px',borderRadius:10,cursor:'pointer',
        border:`2px solid ${selectedType===type?border:'rgba(255,255,255,.12)'}`,
        background:selectedType===type?bg:'rgba(255,255,255,.06)',
        color:'#fff',fontWeight:800,fontSize:12,lineHeight:1.4,
        transition:'all .15s',
        boxShadow:selectedType===type?`0 0 14px ${border}44`:'none',
      }}>
      {label}<br/>
      <span style={{fontSize:10,fontWeight:400,opacity:.8}}>{payout}</span>
    </button>
  )

  return(
    <div style={{
      display:'flex',flexDirection:'column',height:'100%',maxWidth:480,margin:'0 auto',
      background:'#0f0f1a',color:'#fff',fontFamily:'system-ui,sans-serif',
      userSelect:'none',overflow:'hidden',position:'relative',
    }}>
      <style>{`
        @keyframes cpToast{0%{opacity:0;transform:translateX(-50%) scale(.85)}15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes cpWin{0%{transform:scale(.5);opacity:0}50%{transform:scale(1.2)}100%{transform:scale(1);opacity:1}}
        @keyframes cpPop{0%{transform:scale(.8)}60%{transform:scale(1.1)}100%{transform:scale(1)}}
        @keyframes cpShine{0%,100%{opacity:.8}50%{opacity:1}}
        @keyframes cpBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        .cp-btn:hover{filter:brightness(1.15)!important}
        .cp-btn:active{transform:scale(.94)!important}
        .cp-num:hover{transform:scale(1.08)!important}
      `}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'8px 14px',background:'rgba(0,0,0,.6)',
        borderBottom:'1px solid rgba(255,255,255,.06)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:18}}>🎨</span>
          <strong style={{fontSize:13,letterSpacing:.5}}>COLOR PREDICTION</strong>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.3)',
            borderRadius:8,padding:'4px 10px',fontWeight:700,color:'#c9a84c',fontSize:13}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:56,left:'50%',zIndex:60,
          animation:'cpToast 2.5s ease-in-out forwards',pointerEvents:'none',
          background:'rgba(0,0,0,.92)',color:'#fff',padding:'7px 18px',
          borderRadius:20,fontSize:13,fontWeight:700,whiteSpace:'nowrap',
          border:'1px solid rgba(255,255,255,.15)'}}>
          {toast}
        </div>
      )}

      {/* Scrollable content */}
      <div style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',gap:0}}>

        {/* ── Timer + Result ──────────────────────────────────────────────── */}
        <div style={{padding:'16px 14px 12px',textAlign:'center',
          background:'linear-gradient(180deg,rgba(0,0,0,.4),transparent)'}}>

          {/* Round ID */}
          <div style={{fontSize:10,color:'rgba(255,255,255,.3)',marginBottom:10,
            letterSpacing:.5}}>
            ROUND #{roundId.slice(-6).toUpperCase()}
          </div>

          {/* Countdown / Result */}
          {phase==='betting'&&(
            <CountdownRing ms={countdownMs} totalMs={30000}/>
          )}

          {phase==='revealing'&&(
            <div style={{height:110,display:'flex',alignItems:'center',
              justifyContent:'center'}}>
              <div style={{fontSize:28,animation:'cpBounce .5s infinite'}}>🎲</div>
            </div>
          )}

          {phase==='result'&&result&&(
            <div style={{height:110,display:'flex',flexDirection:'column',
              alignItems:'center',justifyContent:'center',gap:8}}>
              <div style={{
                width:72,height:72,borderRadius:16,
                background:numBg(result.number),
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:32,fontWeight:900,
                boxShadow:`0 0 30px ${result.colors.includes('red')?'rgba(220,38,38,.6)':result.colors.includes('violet')?'rgba(139,92,246,.6)':'rgba(22,163,74,.6)'}`,
                animation:'cpWin .5s ease-out',
              }}>
                {result.number}
              </div>
              <div style={{display:'flex',gap:4}}>
                {result.colors.map(c=>(
                  <span key={c} style={{
                    fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10,
                    background:c==='red'?'rgba(220,38,38,.3)':c==='green'?'rgba(22,163,74,.3)':'rgba(139,92,246,.3)',
                    color:c==='red'?'#f87171':c==='green'?'#4ade80':'#c4b5fd',
                    border:`1px solid ${c==='red'?'rgba(220,38,38,.5)':c==='green'?'rgba(22,163,74,.5)':'rgba(139,92,246,.5)'}`,
                    textTransform:'uppercase',
                  }}>{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* My win/loss result */}
          {myResult&&winAnim&&(
            <div style={{marginTop:8,padding:'8px 16px',borderRadius:12,
              background:'rgba(74,222,128,.15)',border:'1px solid rgba(74,222,128,.3)',
              animation:'cpPop .4s ease-out',display:'inline-block'}}>
              <span style={{fontSize:16,fontWeight:900,color:'#4ade80'}}>
                🏆 +₹{myResult.total_win.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* ── History ─────────────────────────────────────────────────────── */}
        <div style={{padding:'0 14px 12px'}}>
          <div style={{fontSize:9,color:'rgba(255,255,255,.3)',marginBottom:6,letterSpacing:.5}}>
            HISTORY
          </div>
          <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
            {history.slice(0,20).map((h,i)=>(
              <div key={i} style={{
                width:28,height:28,borderRadius:7,
                background:numBg(h.number),
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:11,fontWeight:800,color:'#fff',
                opacity:1-i*.04,flexShrink:0,
              }}>{h.number}</div>
            ))}
            {history.length===0&&(
              <span style={{fontSize:10,color:'rgba(255,255,255,.2)'}}>No history yet</span>
            )}
          </div>
        </div>

        {/* ── Bet type selector ────────────────────────────────────────────── */}
        <div style={{padding:'0 14px',marginBottom:10}}>
          <div style={{fontSize:9,color:'rgba(255,255,255,.3)',marginBottom:7,letterSpacing:.5}}>
            SELECT COLOR / TYPE
          </div>
          <div style={{display:'flex',gap:6,marginBottom:6}}>
            {colorBtn('red',   '🔴 RED',   'rgba(220,38,38,.35)',  '#ef4444','2×')}
            {colorBtn('violet','🟣 VIOLET','rgba(139,92,246,.35)', '#8b5cf6','4.5×')}
            {colorBtn('green', '🟢 GREEN', 'rgba(22,163,74,.35)',  '#22c55e','2×')}
          </div>
          <div style={{display:'flex',gap:6}}>
            {colorBtn('small','⬇️ SMALL\n1-4', 'rgba(59,130,246,.3)', '#3b82f6','2×')}
            {colorBtn('big',  '⬆️ BIG\n6-9',  'rgba(245,158,11,.3)', '#f59e0b','2×')}
            {colorBtn('number','🔢 NUMBER','rgba(168,85,247,.3)',  '#a855f7','9×')}
          </div>
        </div>

        {/* ── Number grid (shown when type=number) ────────────────────────── */}
        {selectedType==='number'&&(
          <div style={{padding:'0 14px',marginBottom:10}}>
            <div style={{fontSize:9,color:'rgba(255,255,255,.3)',marginBottom:7,letterSpacing:.5}}>
              PICK NUMBER (9×)
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:6}}>
              {Array.from({length:10},(_,i)=>(
                <button key={i} className="cp-num"
                  onClick={()=>setSelectedNum(i)}
                  style={{
                    borderRadius:10,padding:'10px 0',cursor:'pointer',
                    border:selectedNum===i?'2px solid #fff':'2px solid rgba(255,255,255,.15)',
                    background:selectedNum===i?numBg(i):'rgba(255,255,255,.06)',
                    color:'#fff',fontSize:18,fontWeight:900,transition:'all .12s',
                    boxShadow:selectedNum===i?`0 0 12px rgba(255,255,255,.3)`:'none',
                  }}>
                  {i}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Stake selector ───────────────────────────────────────────────── */}
        <div style={{padding:'0 14px',marginBottom:10}}>
          <div style={{fontSize:9,color:'rgba(255,255,255,.3)',marginBottom:7,letterSpacing:.5}}>
            STAKE
          </div>
          <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
            {STAKES.map(sv=>(
              <button key={sv} className="cp-btn"
                onClick={()=>setStake(sv)}
                style={{
                  borderRadius:20,padding:'5px 12px',cursor:'pointer',flexShrink:0,
                  border:stake===sv?'2px solid #c9a84c':'2px solid rgba(255,255,255,.1)',
                  background:stake===sv?'rgba(201,168,76,.2)':'rgba(255,255,255,.05)',
                  color:stake===sv?'#c9a84c':'rgba(255,255,255,.5)',
                  fontSize:12,fontWeight:700,transition:'all .1s',
                }}>
                ₹{sv>=1000?`${sv/1000}K`:sv}
              </button>
            ))}
          </div>
        </div>

        {/* ── Pending bets ─────────────────────────────────────────────────── */}
        {pendingBets.length>0&&(
          <div style={{padding:'0 14px',marginBottom:8}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
              marginBottom:5}}>
              <span style={{fontSize:9,color:'rgba(255,255,255,.3)',letterSpacing:.5}}>
                PENDING BETS
              </span>
              {!betPlaced&&(
                <span onClick={()=>setPendingBets([])}
                  style={{fontSize:9,color:'rgba(239,68,68,.6)',cursor:'pointer'}}>
                  ✕ clear
                </span>
              )}
            </div>
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
              {pendingBets.map((b,i)=>(
                <div key={i} style={{
                  background:'rgba(255,255,255,.08)',borderRadius:8,
                  padding:'3px 8px',fontSize:10,fontWeight:700,
                  border:'1px solid rgba(255,255,255,.15)',
                  color:b.type==='red'?'#f87171':b.type==='green'?'#4ade80':
                    b.type==='violet'?'#c4b5fd':'#fbbf24',
                }}>
                  {b.type==='number'?`#${b.value}`:b.type.toUpperCase()} ₹{b.stake}
                </div>
              ))}
              <div style={{fontSize:10,color:'rgba(255,255,255,.4)',
                alignSelf:'center',marginLeft:4}}>
                Total: ₹{totalStake}
              </div>
            </div>
          </div>
        )}

        {/* Bottom padding */}
        <div style={{height:8}}/>
      </div>

      {/* ── Fixed bottom action bar ──────────────────────────────────────── */}
      <div style={{
        padding:'8px 14px 12px',
        background:'linear-gradient(to top,rgba(0,0,0,.98),rgba(0,0,0,.8))',
        borderTop:'1px solid rgba(255,255,255,.06)',
        flexShrink:0,
      }}>
        {phase==='betting'&&!betPlaced&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <button className="cp-btn" onClick={addBet}
              disabled={countdownMs<3000}
              style={{
                padding:'11px 0',borderRadius:10,border:'none',cursor:'pointer',
                fontWeight:800,fontSize:13,transition:'all .15s',
                background:'rgba(255,255,255,.1)',color:'#fff',
              }}>
              + ADD BET
            </button>
            <button className="cp-btn" onClick={placeBets}
              disabled={pendingBets.length===0}
              style={{
                padding:'11px 0',borderRadius:10,border:'none',
                cursor:pendingBets.length>0?'pointer':'default',
                fontWeight:800,fontSize:13,transition:'all .15s',
                background:pendingBets.length>0
                  ?'linear-gradient(135deg,#7c3aed,#4c1d95)':'rgba(255,255,255,.07)',
                color:pendingBets.length>0?'#fff':'rgba(255,255,255,.2)',
                boxShadow:pendingBets.length>0?'0 4px 16px rgba(124,58,237,.4)':'none',
              }}>
              🎯 CONFIRM ₹{totalStake}
            </button>
          </div>
        )}

        {phase==='betting'&&betPlaced&&(
          <div style={{padding:'11px 0',borderRadius:10,textAlign:'center',
            background:'rgba(74,222,128,.1)',border:'1px solid rgba(74,222,128,.3)',
            color:'#4ade80',fontWeight:700,fontSize:13}}>
            ✅ Bet confirmed — waiting for result...
          </div>
        )}

        {phase==='revealing'&&(
          <div style={{padding:'11px 0',borderRadius:10,textAlign:'center',
            background:'rgba(245,158,11,.1)',border:'1px solid rgba(245,158,11,.3)',
            color:'#f59e0b',fontWeight:700,fontSize:13,
            animation:'cpShine 1s infinite'}}>
            🎲 Drawing result...
          </div>
        )}

        {phase==='result'&&(
          <div style={{padding:'11px 0',borderRadius:10,textAlign:'center',
            background:'rgba(255,255,255,.05)',
            color:'rgba(255,255,255,.4)',fontSize:12}}>
            Next round starting soon...
          </div>
        )}
      </div>
    </div>
  )
}
